export interface PathsItemProps {
  label: string;
  href?: string;
  hrefZh?: string;
  ga?: string;
  target?: React.HTMLAttributeAnchorTarget;
  fn?: () => void;
  // 是否加多语言前缀 true
  locale?: string | false;
  // label 形如「名称 (English)」时，把括号里的英文整体换到下一行且不再折行，避免在英文单词中间断开。
  // 仅对设置了该开关的条目生效（否则 en 的 "Learn English (ELA)" 这类文案也会被拆开）
  breakBeforeParen?: boolean;
}

type Paths = {
  label: string;
  children: PathsItemProps[];
}[];

export type FooterLang = 'zh' | 'en';

/**
 * 「Math」专题列：向 /learn-math/ 及其子页面导流站内权重（SEO 需求「/learn-math/上线后站内调整需求」6.2.2）。
 *
 * 1. 锚文本走多语言表格 common 子表（词条 `底部导航-数学` / `底部导航-数学-*`），要改文案去表格改，
 *    不要直接改 public/locales（构建时 prebuild -> translate 会整体覆盖）。
 * 2. 目标 URL、展示顺序以需求文档表格为准。注意中英文顺序并不一致
 *    （中文的 AMC 8 排在袋鼠数学之前），请勿顺手统一。
 * 3. /learn-math/ 由 next.config.js 的 rewrites 代理到 WordPress，这里用站内路径即可，
 *    <Link> 会按当前语言补 /zh 前缀，最终即为需求里的目标 URL。
 */
const getMathColumn = (
  t: (name: string) => string,
  lang: FooterLang,
): Paths[number] => {
  // 中文锚文本形如「袋鼠数学 (Math Kangaroo)」：括号里的英文整体换到下一行（见 Footer 的 renderLabel）
  const link = (label: string, href: string, ga: string): PathsItemProps => ({
    label,
    href,
    ga,
    breakBeforeParen: true,
  });

  const singapore = link(
    t('底部导航-数学-新加坡数学'),
    '/learn-math/singapore-math/',
    'MathSingaporeClick',
  );
  const kangaroo = link(
    t('底部导航-数学-袋鼠数学'),
    '/learn-math/math-kangaroo/',
    'MathKangarooClick',
  );
  const amc8 = link(
    t('底部导航-数学-AMC8'),
    '/learn-math/amc-8/',
    'MathAmc8Click',
  );
  const sat = link(
    t('底部导航-数学-SAT数学'),
    '/learn-math/sat/',
    'MathSatClick',
  );
  const league = link(
    t('底部导航-数学-美国大联盟数学'),
    '/learn-math/math-league/',
    'MathLeagueClick',
  );

  return {
    label: t('底部导航-数学'),
    children:
      lang === 'zh'
        ? [singapore, amc8, kangaroo, sat, league]
        : [singapore, kangaroo, amc8, sat, league],
  };
};

const generatePaths = (
  t: (name: string) => string,
  lang: FooterLang = 'en',
): Paths => {
  return [
    {
      label: t('导航-课程'),
      children: [
        {
          label: t('底部导航-学习中文'),
          href: '/chinese/',
          ga: 'CourseChineseClick',
        },
        {
          label: t('底部导航-学习数学'),
          // 站内权重集中到 /learn-math/（原 /math/ 的 canonical 也已指向它）
          href: '/learn-math/',
          ga: 'CourseMathClick',
        },
        {
          label: t('底部导航-英文阅读写作(ELA)'),
          href: '/english/',
          ga: 'CourseEnglishClick',
        },
      ],
    },
    // Courses 之后新增 Math 列，需直接可见（不折叠）
    getMathColumn(t, lang),
    {
      label: t('导航-关于我们'),
      children: [
        { label: t('导航-关于悟空'), href: '/aboutus/', ga: 'CompanyClick' },
        { label: t('导航-媒体新闻'), href: '/news', ga: '' },
        { label: t('导航-联系我们'), href: '/contactus', ga: 'ContactusClick' },
        { label: t('导航-常见问题'), href: '/faq/', ga: 'FaqClick' },
        // { label: t('导航-企业历程'), href: '/aboutus/', ga: 'CompanyClick' },
        // { label: t('导航-师资力量'), href: '/teachers/', ga: 'TeacherClick' },
        // { label: t('导航-教研团队'), href: '/', ga: '' },
        // {
        //   label: t('导航-博客'),
        //   href: 'https://www.wukongsch.com/blog/',
        //   hrefZh: 'https://www.wukongsch.com/blog/zh/',
        //   ga: 'BlogClick',
        //   locale: false,
        //   target: '_blank',
        // },
        // { label: t('导航-商务合作'), href: '/joinus/#wukong-cooperation', ga: 'BusinessClick' },
        // { label: t('导航-加入我们'), href: '/joinus/#wukong-zhaopin', ga: 'JoinusClick' },
      ],
    },
    // {
    //   label: t('导航-学习园地'),
    //   children: [
    //     { label: t('导航-中文资料'), href: '/', ga: '' },
    //     { label: t('导航-数学资料'), href: '/', ga: '' },
    //     { label: t('导航-英文资料'), href: '/', ga: '' },
    //     { label: t('导航-拓展资料'), href: '/', ga: '' },
    //     { label: t('导航-专题推荐'), href: '/', ga: '' },
    //   ],
    // },
    {
      label: t('导航-探索更多'),
      children: [
        {
          label: t('导航-加入悟空'),
          href: '/joinus/#wukong-zhaopin',
          ga: 'JoinusClick',
        },
        {
          label: t('导航-博客'),
          href: 'https://www.wukongsch.com/blog/',
          hrefZh: 'https://www.wukongsch.com/blog/zh/',
          ga: 'BlogClick',
          locale: false,
          target: '_blank',
        },
        // { label: t('导航-联系我们'), href: '/aboutus/#contant', ga: 'ContactusClick' },
      ],
    },
  ];
};

export default generatePaths;
